import { KeyRound, UserCheck, UserX } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';

import { useSendCustomerPasswordReset, useSetCustomerStatus } from '../api/customers.queries';
import type { Customer } from '../types/customer.types';

type Action = 'deactivate' | 'reactivate' | 'reset';

const TEXT: Record<Action, { title: string; description: string; confirm: string }> = {
  deactivate: {
    title: 'Deactivate this account?',
    description:
      'They can no longer sign in, and their current session cannot be renewed. A session already open ends when its access token expires (at most about a day). Their orders and history are kept.',
    confirm: 'Deactivate',
  },
  reactivate: {
    title: 'Reactivate this account?',
    description: 'They can sign in again with their current password.',
    confirm: 'Reactivate',
  },
  reset: {
    title: 'Send a password reset?',
    description:
      "The customer receives the storefront's own reset code by SMS or email, depending on their country, and chooses a new password themselves. You never see or set it.",
    confirm: 'Send reset code',
  },
};

/** Account actions (customers.manage): reason required, recorded in the audit log. */
export function CustomerAccountActions({ customer }: { customer: Customer }) {
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const status = useSetCustomerStatus(customer.id);
  const reset = useSendCustomerPasswordReset(customer.id);
  const error = action === 'reset' ? reset.error : status.error;

  const open = (next: Action) => {
    status.reset();
    reset.reset();
    setReason('');
    setAction(next);
  };
  const close = () => setAction(null);
  const confirm = () => {
    const trimmed = reason.trim();
    if (action === 'reset') reset.mutate(trimmed, { onSuccess: close });
    else if (action)
      status.mutate({ isActive: action === 'reactivate', reason: trimmed }, { onSuccess: close });
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {customer.isActive && (
          <Button size="sm" variant="outline" onClick={() => open('reset')}>
            <KeyRound className="size-3.5" aria-hidden />
            Send password reset
          </Button>
        )}
        {customer.isActive ? (
          <Button size="sm" variant="destructive" onClick={() => open('deactivate')}>
            <UserX className="size-3.5" aria-hidden />
            Deactivate
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => open('reactivate')}>
            <UserCheck className="size-3.5" aria-hidden />
            Reactivate
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={action !== null}
        onOpenChange={(isOpen) => !isOpen && close()}
        title={action ? TEXT[action].title : ''}
        description={action ? TEXT[action].description : ''}
        confirmLabel={action ? TEXT[action].confirm : ''}
        variant={action === 'deactivate' ? 'destructive' : 'default'}
        isPending={status.isPending || reset.isPending}
        confirmDisabled={reason.trim().length < 3}
        onConfirm={confirm}
      >
        <div className="space-y-1.5">
          <Label htmlFor="customer-action-reason" className="text-xs">
            Reason <span className="text-muted-foreground font-normal">(required)</span>
          </Label>
          <textarea
            id="customer-action-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={2}
            maxLength={500}
            className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1.5 text-xs focus-visible:ring-2 focus-visible:outline-none"
          />
          {error instanceof ApiError && (
            <p role="alert" className="text-destructive text-xs">
              {error.message}
            </p>
          )}
        </div>
      </ConfirmDialog>
    </>
  );
}
